import React, { useEffect, useRef } from "react";

import useArticleSection from "@FillInTableContainer/hooks/useArticleSection";
import {
  TableDividerPlusBar,
  TableCreatorTableTitle,
  FillInTableTableCreatorTitleRowContainer,
} from "@FillInTableContainer/shared";
import {
  GroupTableKeyType,
  TableCreatorGroupShellList,
} from "@FillInTableContainer/shared/types/tableCreator.types";
import {
  isAddDeleteTable,
  isGroupShellScroll,
  isSeparatedShellTitle,
  isTableScroll,
} from "@FillInTableContainer/shared/utils/getOptionsFromData";
import { getTableTitle } from "@FillInTableContainer/shared/utils/getTextFromData";
import IconXMark from "@images/fillInTable/tableCreator/icon-x-mark-white.svg";
import { newArrayToCountValue } from "@utils/newArrayToCountValue";

import * as S from "./ClinicalTrialsTableCreatorTable.style";
import ClinicalTrialsTableCreatorGroupShell from "./components/ClinicalTrialsTableCreatorGroupShell/ClinicalTrialsTableCreatorGroupShell";
import TableCreatorTableCustomTitleInput from "./components/TableCreatorTableCustomTitleInput/TableCreatorTableCustomTitleInput";
import { useHandleClinicalTrialsTableCreatorTable } from "./hooks/useHandleClinicalTrialsTableCreatorTable";

import type { ClinicalTrialsSection } from "@/types/clinicalTrials.types";

export interface TableInfoProps {
  groupTableKey: ClinicalTrialsSection;
  tableKey: string | null;
  groupShellList: TableCreatorGroupShellList;
  groupTableIndex: number;
  tableIndex: number;
  tableCount: number;
}
interface Props extends TableInfoProps {
  handleAddTable: (tableIndex: number) => void;
  handleDragStart: (
    event: React.DragEvent<HTMLDivElement>,
    index: number,
  ) => void;
  handleDragEnd: (groupTableKey: GroupTableKeyType) => void;
  handleDragOn: () => void;
  isDraggableTable: boolean;
}

const ClinicalTrialsTableCreatorTable = ({
  groupTableKey,
  tableKey,
  groupTableIndex,
  tableIndex,
  tableCount,
  groupShellList,
  handleAddTable,
  handleDragEnd,
  handleDragOn,
  handleDragStart,
  isDraggableTable,
}: Props) => {
  const articleSection = useArticleSection();
  const tablePropsInfo: TableInfoProps = {
    groupTableKey,
    tableKey,
    groupTableIndex,
    tableIndex,
    tableCount,
    groupShellList,
  };
  const tableTitleRef = useRef<HTMLDivElement>(null);
  const separatedShellTitleRef = useRef<HTMLDivElement>(null);

  const {
    focusStatus,
    isFocused,
    isLastTable,
    tableWrapperRef,
    tableCustomTitle,
    isVisibleTableCustomTitle,
    isFocusedAddButton,
    handleClickDeleteButton,
    focusEventToChangePositionInfo,
    numberOfGroupShellCount,
    changeFocusStatus,
    changePositionInfo,
    changePositionInfoToDefault,
    changeInfoToCurrentTable,
    handleDefaultFocus,
  } = useHandleClinicalTrialsTableCreatorTable(tablePropsInfo);

  const isClearTable =
    isFocused &&
    focusStatus.type === "clear" &&
    focusStatus.container === "table";

  const isDeleteTable =
    isFocused &&
    focusStatus.type === "delete" &&
    focusStatus.container === "table";

  const isTableScrollWrapper = isTableScroll(articleSection, tableKey);

  const handleFocusTableTitle = () => {
    focusEventToChangePositionInfo({
      type: "clear",
      container: "table",
    });
  };

  useEffect(() => {
    const handleCheckClickOutsideShell = (e: MouseEvent) => {
      if (isFocused) {
        const target = e.target as HTMLElement;

        const clickSelectButton = tableTitleRef.current?.contains(target);

        if (clickSelectButton) return;
        if (target.dataset.button) return;
        if (target.dataset.title) return;

        handleDefaultFocus();
      }
    };

    window.addEventListener("click", handleCheckClickOutsideShell);

    return () =>
      window.removeEventListener("click", handleCheckClickOutsideShell);
  }, [handleDefaultFocus, isFocused]);

  return (
    <>
      {isAddDeleteTable(articleSection, tableKey) && (
        <TableDividerPlusBar
          tableTitle={getTableTitle(articleSection, tableKey) || ""}
          isLastPosition={false}
          onClick={() => handleAddTable(tableIndex)}
          isVisibility={isFocusedAddButton}
        />
      )}
      <S.TableWrapper
        className="table-wrapper"
        isFocused={isFocused}
        isTableScroll={isTableScrollWrapper}
        isClearTable={isClearTable}
        isDeleteTable={isDeleteTable}
        isAddTable={isAddDeleteTable(articleSection, tableKey)}
        isDraggingTable={isFocused && focusStatus.type === "dragging"}
      >
        {(isClearTable || isDeleteTable) && (
          <S.DeleteTableButtonWrapper>
            <S.DeleteTableButton
              data-button="delete-button"
              onClick={handleClickDeleteButton}
            >
              <img src={IconXMark} alt="button for clear table" />
              {focusStatus.type === "clear"
                ? `Clear ${
                    [
                      "Clinical Endpoints (Table)",
                      "Clinical Trial Design-1",
                      "Clinical Trial Design-2",
                    ].includes(groupTableKey)
                      ? "Table"
                      : ""
                  }`
                : null}
              {focusStatus.type === "delete"
                ? `Delete ${
                    [
                      "Clinical Endpoints (Table)",
                      "Clinical Trial Design-1",
                      "Clinical Trial Design-2",
                    ].includes(groupTableKey)
                      ? "Table"
                      : ""
                  }`
                : null}
            </S.DeleteTableButton>
          </S.DeleteTableButtonWrapper>
        )}
        <S.ScrollWrapper
          isTableScroll={isTableScrollWrapper}
          ref={tableWrapperRef}
        >
          <S.ScrollContentsWrapper>
            <div ref={tableTitleRef}>
              {getTableTitle(articleSection, tableKey) && (
                <TableCreatorTableTitle
                  tableKey={tableKey}
                  groupTableKey={groupTableKey}
                  groupTableIndex={groupTableIndex}
                  tableIndex={tableIndex}
                  isDragging={isFocused && focusStatus.type === "dragging"}
                  onClickHandler={handleFocusTableTitle}
                  handleDragStart={handleDragStart}
                  handleDragEnd={handleDragEnd}
                  handleDragOn={handleDragOn}
                  isDraggableTable={isDraggableTable}
                  changeFocusStatusForDragging={() => {
                    changePositionInfo();
                    changeFocusStatus({
                      type: "dragging",
                      container: "table",
                    });
                  }}
                  changeFocusStatusForDragEnd={() => {
                    changePositionInfoToDefault();
                    changeFocusStatus({
                      type: "default",
                      container: "noFocus",
                    });
                  }}
                />
              )}
            </div>
            {isVisibleTableCustomTitle && (
              <TableCreatorTableCustomTitleInput
                data-title="custom-title"
                customTitle={tableCustomTitle}
                isClearTable={isClearTable}
                changeInfoToCurrentTable={changeInfoToCurrentTable}
              />
            )}
            {groupShellList.map(
              ({ groupShellKey, shellList }, groupShellListIndex) => (
                <S.GroupShellWrapper
                  key={`groupShellList-${String(groupShellListIndex)}`}
                >
                  {newArrayToCountValue(
                    numberOfGroupShellCount(groupShellKey),
                  ).map((_, groupShellIndex) => {
                    return (
                      <>
                        {isSeparatedShellTitle(articleSection, groupShellKey) &&
                          groupShellIndex === 0 && (
                            <div ref={separatedShellTitleRef}>
                              <FillInTableTableCreatorTitleRowContainer
                                positionPropsInfo={{
                                  groupTableKey,
                                  groupTableIndex,
                                  groupShellKey,
                                  groupShellIndex,
                                  tableKey,
                                  tableIndex,
                                }}
                                shellList={shellList}
                                isTableScroll={
                                  isTableScroll(articleSection, tableKey) ||
                                  isGroupShellScroll(
                                    articleSection,
                                    groupShellKey,
                                  )
                                }
                              />
                            </div>
                          )}

                        <ClinicalTrialsTableCreatorGroupShell
                          key={`group-shell-${String(groupTableIndex)}-${String(
                            tableIndex,
                          )}-${String(groupShellIndex)}`}
                          shellList={shellList}
                          groupTableKey={groupTableKey}
                          groupTableIndex={groupTableIndex}
                          tableKey={tableKey}
                          tableIndex={tableIndex}
                          groupShellKey={groupShellKey}
                          groupShellIndex={groupShellIndex}
                          groupShellCount={numberOfGroupShellCount(
                            groupShellKey,
                          )}
                          isTableScroll={
                            isTableScroll(articleSection, tableKey) ||
                            isGroupShellScroll(articleSection, groupShellKey)
                          }
                        />
                      </>
                    );
                  })}
                </S.GroupShellWrapper>
              ),
            )}
          </S.ScrollContentsWrapper>
        </S.ScrollWrapper>
      </S.TableWrapper>
      {isAddDeleteTable(articleSection, tableKey) && (
        <TableDividerPlusBar
          tableTitle={getTableTitle(articleSection, tableKey) || ""}
          onClick={() => handleAddTable(tableIndex + 1)}
          isLastPosition
          isVisibility={isLastTable && isFocusedAddButton}
        />
      )}
    </>
  );
};

export default ClinicalTrialsTableCreatorTable;
